import { useEffect } from "react";
import type { RefObject } from "react";
import type { Rfq } from "../../domain/rfq-types";

type Props = {
  rfq: Rfq | null; composeOpen: boolean;
  invitationDrawerSupplierId: string | null; invitationConfirm: "resend" | "revoke" | null;
  workspaceRef: RefObject<HTMLElement | null>; drawerRef: RefObject<HTMLElement | null>;
  composeDrawerRef: RefObject<HTMLElement | null>; confirmationCancelRef: RefObject<HTMLButtonElement | null>;
  closeComposeDrawer: () => void; closeInvitationDrawer: () => void; closeInvitationConfirmation: () => void;
};
export function useRfqDrawerEffects({
  rfq,
  composeOpen,
  invitationDrawerSupplierId,
  invitationConfirm,
  workspaceRef,
  drawerRef,
  composeDrawerRef,
  confirmationCancelRef,
  closeInvitationConfirmation,
  closeInvitationDrawer,
  closeComposeDrawer,
}: Props) {
  useEffect(() => {
    if (!composeOpen || rfq) return;
    const workspace = workspaceRef.current;
    workspace?.setAttribute("inert", "");
    workspace?.setAttribute("aria-hidden", "true");
    const drawer = composeDrawerRef.current;
    drawer
      ?.querySelector<HTMLElement>("[data-compose-initial]")
      ?.focus({ preventScroll: true });
    const drawerBody = drawer?.querySelector<HTMLElement>(
      ".rfq-create-drawer-body",
    );
    if (drawerBody) drawerBody.scrollTop = 0;
    const resetScrollFrame = window.requestAnimationFrame(() => {
      if (drawerBody) drawerBody.scrollTop = 0;
    });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeComposeDrawer();
        return;
      }
      if (event.key !== "Tab" || !drawer) return;
      const focusable = Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])',
        ),
      );
      if (!focusable.length) return;
      const first = focusable[0],
        last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(resetScrollFrame);
      document.removeEventListener("keydown", onKeyDown);
      workspace?.removeAttribute("inert");
      workspace?.removeAttribute("aria-hidden");
    };
  }, [composeOpen, rfq]);
  useEffect(() => {
    if (!invitationDrawerSupplierId) return;
    const workspace = workspaceRef.current;
    workspace?.setAttribute("inert", "");
    workspace?.setAttribute("aria-hidden", "true");
    const drawer = drawerRef.current;
    drawer?.querySelector<HTMLElement>("button")?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      const confirmation = drawer?.querySelector<HTMLElement>(
        '[role="alertdialog"]',
      );
      if (event.key === "Escape") {
        event.preventDefault();
        if (confirmation) closeInvitationConfirmation();
        else closeInvitationDrawer();
        return;
      }
      if (event.key !== "Tab" || !drawer) return;
      const focusScope = confirmation ?? drawer;
      const focusable = Array.from(
        focusScope.querySelectorAll<HTMLElement>(
          'button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
        ),
      );
      if (!focusable.length) return;
      const first = focusable[0],
        last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      workspace?.removeAttribute("inert");
      workspace?.removeAttribute("aria-hidden");
    };
  }, [invitationDrawerSupplierId]);
  useEffect(() => {
    if (invitationConfirm) confirmationCancelRef.current?.focus();
  }, [invitationConfirm]);
}
